import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CharAutocompleteMenu.module.css';

// CharAutocompleteMenu — popup driven by the CharAutocomplete extension.
// Position is snapshotted on each lifecycle event so hover-driven re-renders
// don't re-poll `clientRect` and unsettle the anchor.
//
// Accept keys: Enter, Tab. Escape closes. ArrowUp/Down navigate. Space is
// handled by the extension itself (auto-wrap on exact match) and never
// reaches this component while the menu is open.
export default function CharAutocompleteMenu({ apiRef }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ left: 0, top: 0, anchor: 'top' });
  const [index, setIndex] = useState(0);
  const commandRef = useRef(null);
  const indexRef = useRef(0);
  const itemsRef = useRef([]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const api = {
      setProps: (next) => {
        commandRef.current = next.command;
        const nextItems = next.items || [];
        setItems(nextItems);
        itemsRef.current = nextItems;
        setIndex(0);
        indexRef.current = 0;
        if (next.clientRect) {
          const rect = next.clientRect();
          if (rect) {
            // Flip above the caret when there isn't enough room below.
            // The menu's max-height is 240px; reserve a small viewport margin.
            const menuMaxH = 240;
            const margin = 8;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const flipUp = spaceBelow < menuMaxH + margin && spaceAbove > spaceBelow;
            setPos(
              flipUp
                ? { left: rect.left, top: rect.top - 6, anchor: 'bottom' }
                : { left: rect.left, top: rect.bottom + 6, anchor: 'top' },
            );
          }
        }
        setOpen(true);
      },
      onKeyDown: (event) => {
        const list = itemsRef.current;
        if (!list.length) return false;
        if (event.key === 'ArrowDown') {
          const next = (indexRef.current + 1) % list.length;
          setIndex(next);
          indexRef.current = next;
          return true;
        }
        if (event.key === 'ArrowUp') {
          const next = (indexRef.current - 1 + list.length) % list.length;
          setIndex(next);
          indexRef.current = next;
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          const item = list[indexRef.current];
          if (item) commandRef.current?.(item);
          return true;
        }
        if (event.key === 'Escape') {
          setOpen(false);
          return true;
        }
        return false;
      },
      close: () => setOpen(false),
    };
    apiRef.current = api;
  }, [apiRef]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.menu}
      style={
        pos.anchor === 'bottom'
          ? { left: pos.left, bottom: window.innerHeight - pos.top }
          : { left: pos.left, top: pos.top }
      }
      role="listbox"
    >
      <div className={styles.section}>Character</div>
      {items.length === 0 && <div className={styles.empty}>No matches</div>}
      {items.map((it, i) => {
        const active = i === index;
        return (
          <div
            key={it.name}
            className={`${styles.item} ${active ? styles.itemActive : ''}`}
            onMouseEnter={() => setIndex(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              commandRef.current?.(it);
            }}
            role="option"
            aria-selected={active}
          >
            <span className={styles.itemLabel}>{it.label || it.name}</span>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
